import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createAction } from "@reduxjs/toolkit";
import { TURNSTILE_ENABLED } from "utils/flags";

const AUTH_REQUIRED_PATHS = ["updateEnrollment"];

export async function getFreshTurnstileToken(api) {
  if (process.env.REACT_APP_CF_TURNSTILE_ENABLED !== 'true') {
    throw new Error("Turnstile not enabled");
  }
  if (!process.env.REACT_APP_CF_TURNSTILE_SITE_KEY) {
    throw new Error("Turnstile not configured");
  }

  if (!window.turnstile) throw new Error("Turnstile ref not available");

  const timeoutMs = 30000;

  window.turnstile.reset();

  const token = await new Promise((resolve, reject) => {
    const start = Date.now();

    const checkToken = () => {
      const value = window.turnstile.getResponse();
      if (value) return resolve(value);
      if (Date.now() - start > timeoutMs) return reject(new Error("Timed out waiting for Turnstile token"));
      requestAnimationFrame(checkToken);
    };

    checkToken();
  })

  return token;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_API_URL,
});

const authenticatedBaseQuery = async (args, api, extraOptions) => {
  let token = await getFreshTurnstileToken(api);

  const res = await api.dispatch(
    enrollmentApi.endpoints.verifyHuman.initiate(JSON.stringify({ token: token }))
  );
  const withAuth = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_API_URL,
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${res?.data?.access_token}`);
      return headers;
    },
  });

  return withAuth(args, api, extraOptions);
};

const selectBaseQuery = (endpoint) => {
  const requiresAuth = AUTH_REQUIRED_PATHS.some((path) => endpoint.includes(path));

  return TURNSTILE_ENABLED && requiresAuth ? authenticatedBaseQuery : rawBaseQuery;
};

export const baseQuery = async (args, api, extraOptions) => {
  const endpoint = api?.endpoint || "";
  return selectBaseQuery(endpoint)(args, api, extraOptions);
};

export const enrollmentApi = createApi({
  reducerPath: "enrollmentApi",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getTdspOffers: builder.mutation({
      query: (data) => ({
        url: "/enrollment/get_tdsps_and_offers",
        method: "POST",
        body: data,
      }),
    }),
    uploadFiles: builder.mutation({
      query: (data) => ({
        url: "/enrollment/document/upload",
        method: "POST",
        body: data,
      }),
    }),
    updateEnrollment: builder.mutation({
      query: (data) => ({
        url: "/enrollment/update",
        method: "POST",
        body: data,
      }),
    }),
    getEnrollment: builder.mutation({
      query: (data) => ({
        url: "/enrollment/get",
        method: "POST",
        body: data,
      }),
    }),
    getDeposit: builder.mutation({
      query: (data) => ({
        url: "/enrollment/getdeposit",
        method: "POST",
        body: data,
      }),
    }),
    getServiceAddresses: builder.mutation({
      query: (data) => ({
        url: "/enrollment/meterLookup/search",
        method: "POST",
        body: data,
      }),
    }),
    verifyHuman: builder.mutation({
      query: (data) => ({
        url: "/enrollment/verify_human",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (arg, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.access_token) {
            console.log("Human verified passed");
          }
        } catch (error) {
          console.error("Human verification failed:", error);
        }
      },
    }),
    getEstStartDate: builder.mutation({
      query: (data) => ({
        url: "/enrollment/estimated_start_date",
        method: "POST",
        body: data,
      }),
    }),
    validateSalesChannel: builder.mutation({
      query: (data) => ({
        url: "/enrollment/validate/sales_channel_code",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useVerifyHumanMutation,
  useGetTdspOffersMutation,
  useUploadFilesMutation,
  useUpdateEnrollmentMutation,
  useGetEnrollmentMutation,
  useGetDepositMutation,
  useGetServiceAddressesMutation,
  useGetEstStartDateMutation,
  useValidateSalesChannelMutation,
} = enrollmentApi;

export const resetApp = createAction("app/Reset");
